import { useEffect, useRef, useState } from 'react'

/**
 * Dispatch on the document to drop every tracked hover. A portal closing under a stationary
 * pointer produces no pointer event at all, so whatever it covered would otherwise keep the
 * hover it had while the portal was open. Fire it as the portal goes away; the next pointer
 * event re-claims.
 */
export const HOVER_RESET_EVENT = 'morta:hover-reset'

/**
 * Tracks whether the pointer is over a component, counting anything it renders through a
 * portal (a dropdown, a context menu) as inside it. Returns the flag and a `claim` callback
 * to wire up as `onMouseOver` / `onMouseMove`, which React dispatches along the React tree -
 * so a portalled dropdown claims the row that opened it, and no other.
 *
 * Enter/leave can't do this: a dropdown unmounts under the pointer, its row never gets a
 * leave, and the hover sticks. So every pointer event re-decides it instead - capture-phase
 * listeners clear the claim before React dispatches, the component's handlers re-claim it,
 * and bubble-phase listeners settle the state afterwards.
 */

export default function useHovered(): [boolean, () => void] {
  const [hovered, setHovered] = useState<boolean>(false)
  const claimedRef = useRef<boolean>(false)

  const claim = (): void => {
    claimedRef.current = true
    setHovered(true)
  }

  useEffect(() => {
    if (!hovered) return

    const clearClaim = (): void => {
      claimedRef.current = false
    }
    const settle = (): void => {
      setHovered(claimedRef.current)
    }
    // Leaving the window, or a portal closing, produces no pointer event to settle on.
    const release = (): void => {
      claimedRef.current = false
      setHovered(false)
    }

    // mousemove as well as mouseover: moving within one element crosses no boundary.
    document.addEventListener('mouseover', clearClaim, true)
    document.addEventListener('mousemove', clearClaim, true)
    document.addEventListener('mouseover', settle)
    document.addEventListener('mousemove', settle)
    document.addEventListener('mouseleave', release)
    document.addEventListener(HOVER_RESET_EVENT, release)

    return () => {
      document.removeEventListener('mouseover', clearClaim, true)
      document.removeEventListener('mousemove', clearClaim, true)
      document.removeEventListener('mouseover', settle)
      document.removeEventListener('mousemove', settle)
      document.removeEventListener('mouseleave', release)
      document.removeEventListener(HOVER_RESET_EVENT, release)
    }
  }, [hovered])

  return [hovered, claim]
}
